import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = '/api';

export interface UniversitySettings {
  universityName: string;
  contactEmail: string;
  phoneNumber: string;
  address: string;
  psychologyEmail: string;
  counselingEmail: string;
  mentalHealthEmail: string;
  weekdayHours: string;
  weekendHours: string;
}

export interface AssessmentSettings {
  defaultTimeLimit: number;
  highRiskThreshold: number;
  moderateRiskThreshold: number;
  allowRetakes: boolean;
  requireCompletion: boolean;
  generateWeeklySummary: boolean;
  generateMonthlyAnalytics: boolean;
  generateQuarterlyReports: boolean;
  assessmentRetentionMonths: number;
  activityLogRetentionMonths: number;
}

export interface NotificationSettings {
  highRiskAlerts: boolean;
  assessmentCompletionAlerts: boolean;
  weeklySummaryEmails: boolean;
  maintenanceAlerts: boolean;
  adminAlertEmail: string;
  highRiskAlertDelay: number;
  assessmentReminderDays: number;
  overdueAlertDays: number;
  enableEmergencyContact: boolean;
  emergencyContactNumber: string;
}

export interface SecuritySettings {
  minPasswordLength: number;
  requireSpecialChars: boolean;
  requireNumbers: boolean;
  requireMixedCase: boolean;
  passwordExpiryDays: number;
  sessionTimeoutMinutes: number;
  maxLoginAttempts: number;
  lockoutDurationMinutes: number;
  enableEncryption: boolean;
  enableAuditLogging: boolean;
  enableTwoFactor: boolean;
}

export interface SystemSettings {
  university: UniversitySettings;
  assessment: AssessmentSettings;
  notification: NotificationSettings;
  security: SecuritySettings;
}

const settingsService = {
  async getSettings(): Promise<SystemSettings> {
    const response = await axios.get(`${API_BASE_URL}/settings`, { headers: getAuthHeader() });
    return response.data;
  },

  async updateUniversitySettings(settings: UniversitySettings): Promise<void> {
    await axios.put(`${API_BASE_URL}/settings/university`, settings, { headers: getAuthHeader() });
  },

  async updateAssessmentSettings(settings: AssessmentSettings): Promise<void> {
    await axios.put(`${API_BASE_URL}/settings/assessment`, settings, { headers: getAuthHeader() });
  },

  async updateNotificationSettings(settings: NotificationSettings): Promise<void> {
    await axios.put(`${API_BASE_URL}/settings/notification`, settings, { headers: getAuthHeader() });
  },

  async updateSecuritySettings(settings: SecuritySettings): Promise<void> {
    await axios.put(`${API_BASE_URL}/settings/security`, settings, { headers: getAuthHeader() });
  },

  async updateAllSettings(settings: SystemSettings): Promise<void> {
    await axios.put(`${API_BASE_URL}/settings`, settings, { headers: getAuthHeader() });
  }
};

export default settingsService;